import { homApi } from '~/redux/apis/hom'
import {
  DetailedUserStatsResponse,
  SubtopicTimesResponse,
  TestPrepPanelStatsResponse,
  UserTimeParams,
  UserTimeResponse,
} from '~/statistics-v2/types/statistics.types'

export const curriculumProgressApi = homApi.injectEndpoints({
  endpoints: (builder) => ({
    getUserStats: builder.query<DetailedUserStatsResponse, { countryCode: string; userId?: number }>({
      query: ({ countryCode, userId }) => ({
        url: `/v1/progress/get-user-stats?countryCode=${countryCode}${userId ? `&userId=${userId}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['userStats'],
    }),
    getUserTime: builder.query<UserTimeResponse, UserTimeParams & { userId?: number }>({
      query: ({ countryCode, gradeId, topicOrder, subtopicLevel, userId }) => {
        const params = new URLSearchParams({ countryCode })
        if (gradeId) params.append('gradeId', gradeId)
        if (topicOrder !== undefined) params.append('topicOrder', String(topicOrder))
        if (subtopicLevel) params.append('subtopicLevel', subtopicLevel)
        if (userId) params.append('userId', String(userId))
        return { url: `/v1/progress/get-user-time?${params.toString()}`, method: 'GET' }
      },
      providesTags: ['userStats'],
    }),
    /**
     * Every subtopic's time at once, for the subtopic cards in the topic drill-down.
     *
     * ⚠ Deliberately NOT part of `getUserStats`: hom-api #1935 moved time out of that payload because
     * computing it per node made the panel slow. This is the batched replacement — one request for the
     * whole curriculum, not one per subtopic, so expanding subtopics costs nothing extra.
     *
     * Keys carry the country and grade, so a single response serves every grade the panel switches to.
     */
    getUserSubtopicTimes: builder.query<SubtopicTimesResponse, { userId?: number }>({
      query: ({ userId }) => {
        const params = new URLSearchParams()
        if (userId) params.append('userId', String(userId))
        const query = params.toString()
        return { url: `/v1/progress/get-user-subtopic-times${query ? `?${query}` : ''}`, method: 'GET' }
      },
      providesTags: ['userStats'],
    }),
    getUserRecentCurriculumCountry: builder.query<{ countryCode: string }, { familyMemberId?: number }>({
      query: ({ familyMemberId }) => ({
        url: `/v1/progress/get-recent-curriculum${familyMemberId ? `?familyMemberId=${familyMemberId}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['userStats'],
    }),
    getTestPrepPanelStats: builder.query<TestPrepPanelStatsResponse, { familyMemberId?: number }>({
      query: ({ familyMemberId }) => ({
        url: `/v1/test-generator/panel-stats${familyMemberId ? `?familyMemberId=${familyMemberId}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['userStats'],
    }),
    getDashboardStats: builder.query<
      { timeSpent: number; activities: number; accuracy: number; videoWatchTime: number },
      void
    >({
      query: () => ({ url: '/v1/progress/get-dashboard-stats', method: 'GET' }),
      providesTags: ['userStats'],
    }),
  }),
})

export const {
  useGetUserStatsQuery,
  useGetUserSubtopicTimesQuery,
  useGetUserTimeQuery,
  useGetUserRecentCurriculumCountryQuery,
  useGetTestPrepPanelStatsQuery,
  useGetDashboardStatsQuery,
} = curriculumProgressApi
